import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useLayoutEffect,
  useRef
} from "react";
import {
  useAppSelector,
  useAppDispatch,
  useMap,
  useObjectLiveLocation,
  useFitMapToVisibleUnits
} from "hooks";
import useMapPreferencesPersistence from "hooks/useMapPreferencesPersistence";
import { getHydratedMapPreferences } from "app/data/helpers/mapPreferences.helpers";
import {
  extractValidMapPositions,
  fitMapToVisibleMarkers,
  focusMapOnSingleVehicle,
  isValidMapCoordinate,
  resetMapToOverview
} from "app/data/helpers";
import { setObjectFilters } from "domain/objects/store/objects.slice";
import {
  selectDrawerSelectedAccountId,
  selectGeozonesState,
  selectSpecificObjectState
} from "app/data/selectors";
import {
  getAllGeozonesThunk,
  getObjectDataForMapThunk,
  setActiveObjectId,
  setFilters
} from "app/features";
import { DEFAULT_COORDINATES } from "app/data/constants";
import { isEmpty } from "lodash";
import { MapViewPreferences } from "app/data/types/mapPreferences.types";
import { MapProps, MapStateContextProps } from "./types";

type MapToggles = Pick<
  MapViewPreferences,
  | "showSatelliteMode"
  | "showClusters"
  | "showTraffic"
  | "showGeozones"
  | "autoFitVisibleUnits"
>;

const MapStateContext = createContext<MapStateContextProps | undefined>(
  undefined
);

export const MapStateProvider: React.FC<MapProps> = ({
  children,
  center = DEFAULT_COORDINATES,
  zoom = 8,
  height = "100vh",
  objects = [],
  disableNavigator = true,
  isDashboardMap = false,
  objectsMarker = false,
  showSearch = false,
  showFilter = false,
  geozones,
  radius,
  isLoading = false,
  markers = [],
  initialZoomLevel = 7,
  isObjectDetailsPage = false,
  isHiddenLocation = false,
  enableLastTripsAction = false,
  onOpenLastTrips
}) => {
  const { mapRef, isLoaded, loadError, centerCoords, onMapLoad } = useMap({
    center,
    disableNavigator
  });
  const { allGeozones } = useAppSelector(selectGeozonesState);
  const drawerSelectedAccountId = useAppSelector(selectDrawerSelectedAccountId);
  const dispatch = useAppDispatch();

  const isPrivateRide = isHiddenLocation;
  const isFleetMap = isDashboardMap && !isObjectDetailsPage;
  const previousActiveObjectIdRef = useRef<string | null>(null);

  const { mergeTogglesSync, getCachedSync, canPersist } =
    useMapPreferencesPersistence();

  const [selectedCluster, setSelectedCluster] = useState<any>(null);
  const [showSatelliteMode, setShowSatelliteMode] = useState(false);
  const [showClusters, setShowClusters] = useState(true);
  const [showTraffic, setShowTraffic] = useState(false);
  const [showObjectNames, setShowObjectNames] = useState(false);
  const [showGeozones, setShowGeozones] = useState<boolean>(true);
  const [autoFitVisibleUnits, setAutoFitVisibleUnits] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [isLoadingTiles, setIsLoadingTiles] = useState(false);
  const { activeObjectId } = useAppSelector(selectSpecificObjectState);

  const shouldAutoFitFleet = isFleetMap && autoFitVisibleUnits;
  useFitMapToVisibleUnits(
    mapRef,
    markers,
    shouldAutoFitFleet,
    drawerSelectedAccountId,
    activeObjectId
  );

  const togglesRef = useRef<MapToggles>({
    showSatelliteMode: false,
    showClusters: true,
    showTraffic: false,
    showGeozones: true,
    autoFitVisibleUnits: false
  });

  useObjectLiveLocation(isSelectOpen);

  useEffect(() => {
    window.localStorage.setItem("selectedObjectId", activeObjectId ?? "");
  }, [activeObjectId]);

  useEffect(() => {
    if (activeObjectId) handleMarkerClick(activeObjectId);
  }, [activeObjectId]);

  const applyToggles = useCallback((toggles: MapToggles) => {
    setShowSatelliteMode(toggles.showSatelliteMode);
    setShowClusters(toggles.showClusters);
    setShowTraffic(toggles.showTraffic);
    setShowGeozones(toggles.showGeozones);
    setAutoFitVisibleUnits(toggles.autoFitVisibleUnits);
    togglesRef.current = toggles;
  }, []);

  useLayoutEffect(() => {
    if (!isDashboardMap || !drawerSelectedAccountId) return;

    if (!canPersist) return;

    const { toggles, filters } = getHydratedMapPreferences(getCachedSync());
    applyToggles(toggles);
    dispatch(setFilters(filters));
    dispatch(setObjectFilters(filters));
  }, [
    isDashboardMap,
    canPersist,
    drawerSelectedAccountId,
    getCachedSync,
    applyToggles,
    dispatch
  ]);

  togglesRef.current = {
    showSatelliteMode,
    showClusters,
    showTraffic,
    showGeozones,
    autoFitVisibleUnits
  };

  const persistToggleChange = useCallback(
    (partial: Partial<MapToggles>) => {
      if (!isDashboardMap || !canPersist) return;

      const nextToggles = { ...togglesRef.current, ...partial };
      togglesRef.current = nextToggles;
      mergeTogglesSync(nextToggles);
    },
    [isDashboardMap, canPersist, mergeTogglesSync]
  );

  const setShowSatelliteModePersisted = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      setShowSatelliteMode((prev) => {
        const next = typeof value === "function" ? value(prev) : value;
        persistToggleChange({ showSatelliteMode: next });
        return next;
      });
    },
    [persistToggleChange]
  );

  const setShowClustersPersisted = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      setShowClusters((prev) => {
        const next = typeof value === "function" ? value(prev) : value;
        persistToggleChange({ showClusters: next });
        return next;
      });
    },
    [persistToggleChange]
  );

  const setShowTrafficPersisted = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      setShowTraffic((prev) => {
        const next = typeof value === "function" ? value(prev) : value;
        persistToggleChange({ showTraffic: next });
        return next;
      });
    },
    [persistToggleChange]
  );

  const setShowGeozonesPersisted = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      setShowGeozones((prev) => {
        const next = typeof value === "function" ? value(prev) : value;
        persistToggleChange({ showGeozones: next });
        return next;
      });
    },
    [persistToggleChange]
  );

  const satelliteModeSetter = isDashboardMap
    ? setShowSatelliteModePersisted
    : setShowSatelliteMode;
  const clustersSetter = isDashboardMap
    ? setShowClustersPersisted
    : setShowClusters;
  const trafficSetter = isDashboardMap
    ? setShowTrafficPersisted
    : setShowTraffic;
  const geozonesSetter = isDashboardMap
    ? setShowGeozonesPersisted
    : setShowGeozones;

  useEffect(() => {
    if (!mapRef || !drawerSelectedAccountId) return;

    if (initialZoomLevel && !shouldAutoFitFleet) {
      resetMapToOverview(
        mapRef,
        DEFAULT_COORDINATES,
        initialZoomLevel ?? undefined
      );
    }
  }, [drawerSelectedAccountId, mapRef, initialZoomLevel, shouldAutoFitFleet]);

  useEffect(() => {
    if (mapRef && drawerSelectedAccountId && isDashboardMap && showGeozones) {
      handleShowGeozoneClick();
    }
  }, [drawerSelectedAccountId, mapRef, isDashboardMap, showGeozones]);

  const handleShowGeozoneClick = useCallback(() => {
    if (drawerSelectedAccountId) {
      dispatch(getAllGeozonesThunk(drawerSelectedAccountId));
    }
  }, [dispatch, drawerSelectedAccountId]);

  const onObjectChange = (value: any) => {
    if (isEmpty(value)) return;
    dispatch(setActiveObjectId(null));

    const objectIndex = objects.findIndex((obj: any) => obj.uniqueId === value);
    if (objectIndex < 0) return;

    setIsLoadingTiles(true);

    const selectedObjectLocation = objects[objectIndex].location;

    if (
      selectedObjectLocation &&
      isValidMapCoordinate(
        selectedObjectLocation.latitude,
        selectedObjectLocation.longitude
      )
    ) {
      focusMapOnSingleVehicle(mapRef, {
        lat: selectedObjectLocation.latitude,
        lng: selectedObjectLocation.longitude
      });
    }

    setTimeout(() => {
      handleMarkerClick(objects[objectIndex].uniqueId);
      dispatch(setActiveObjectId(value || ""));
      setIsLoadingTiles(false);
    }, 1000);
  };

  const onTilesLoaded = () => {
    setIsLoadingTiles(false);
  };

  const handleEnableClusterClick = useCallback(() => {
    clustersSetter((prev) => !prev);
  }, [clustersSetter]);

  const handleClusterClick = (cluster: any) => {
    dispatch(setActiveObjectId(null));
    setSelectedCluster(cluster);
  };

  const handleClusterCloseInfoWindow = () => {
    dispatch(setActiveObjectId(null));
    setSelectedCluster(null);
  };

  const handleMarkerClick = useCallback(
    async (markerId: string) => {
      if (isEmpty(markerId)) return;

      window.localStorage.setItem("selectedObjectId", markerId);

      await dispatch(
        getObjectDataForMapThunk({
          accountId: drawerSelectedAccountId || "",
          objectId: markerId
        })
      );
    },
    [dispatch, drawerSelectedAccountId]
  );

  useEffect(() => {
    if (!isFleetMap) return;

    if (previousActiveObjectIdRef.current === activeObjectId) return;

    if (!activeObjectId || !mapRef) {
      previousActiveObjectIdRef.current = activeObjectId ?? null;
      return;
    }

    const marker = markers.find(
      (item: { uniqueId?: string }) => item.uniqueId === activeObjectId
    );
    const [position] = extractValidMapPositions(marker ? [marker] : []);
    if (!position) return;

    focusMapOnSingleVehicle(mapRef, position);
    previousActiveObjectIdRef.current = activeObjectId;
  }, [activeObjectId, mapRef, markers, isFleetMap]);

  const handleAutoFitVisibleUnitsButtonClick = useCallback(() => {
    if (!isFleetMap) return;

    const next = !autoFitVisibleUnits;
    setAutoFitVisibleUnits(next);
    persistToggleChange({ autoFitVisibleUnits: next });

    if (next) {
      fitMapToVisibleMarkers(mapRef, extractValidMapPositions(markers));
    } else {
      resetMapToOverview(mapRef, DEFAULT_COORDINATES, initialZoomLevel ?? 7);
    }
  }, [
    autoFitVisibleUnits,
    initialZoomLevel,
    isFleetMap,
    mapRef,
    markers,
    persistToggleChange
  ]);

  const shouldRenderGeozones = useMemo(
    () => zoom > 10 && showGeozones,
    [zoom, showGeozones]
  );

  return (
    <MapStateContext.Provider
      value={{
        markers,
        showSatelliteMode,
        setShowSatelliteMode: satelliteModeSetter,
        showClusters,
        setShowClusters: clustersSetter,
        showTraffic,
        setShowTraffic: trafficSetter,
        showObjectNames,
        setShowObjectNames,
        showGeozones,
        setShowGeozones: geozonesSetter,
        autoFitVisibleUnits,
        handleEnableClusterClick,
        handleShowGeozoneClick,
        isSelectOpen,
        setIsSelectOpen,
        mapRef,
        isLoaded,
        isLoading,
        isLoadingTiles,
        loadError,
        centerCoords,
        onMapLoad,
        onObjectChange,
        onTilesLoaded,
        zoom,
        height,
        selectedCluster,
        handleClusterClick,
        handleClusterCloseInfoWindow,
        handleMarkerClick,
        handleAutoFitVisibleUnitsButtonClick,
        shouldRenderGeozones,
        allGeozones,
        objectsMarker,
        isDashboardMap,
        showSearch,
        showFilter,
        geozones,
        radius,
        drawerSelectedAccountId,
        activeObjectId,
        isPrivateRide,
        isObjectDetailsPage,
        isHiddenLocation,
        enableLastTripsAction,
        onOpenLastTrips
      }}
    >
      {children}
    </MapStateContext.Provider>
  );
};

export const useMapState = () => {
  const context = useContext(MapStateContext);
  if (!context) {
    throw new Error("useMapState must be used within a MapStateProvider");
  }
  return context;
};
